import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, FileCheck2, AlertTriangle, MessageSquare, Target, ListChecks, Check, RotateCcw } from "lucide-react";
import { SectionHeader } from "@/components/meeting/SectionHeader";

const BulletCard = ({ icon, title, items, emptyLabel, marker, testId }) => (
  <Card className="p-8 bg-af-card border-af rounded-md" data-testid={testId}>
    <SectionHeader icon={icon} title={title} />
    {items?.length ? (
      <ul className="space-y-2.5">
        {items.map((it) => (
          <li key={it} className="flex gap-2.5 text-sm text-af leading-relaxed">
            {marker && <span className={marker.cls}>{marker.char}</span>}
            <span>{marker ? it : `• ${it}`}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-af-muted">{emptyLabel}</p>
    )}
  </Card>
);

const ActionItemsTable = ({ actions, onToggle }) => (
  <div className="overflow-x-auto -mx-2">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-af-subtle border-y border-af text-af-secondary">
          <th className="text-left px-3 py-2 font-semibold w-16">Statut</th>
          <th className="text-left px-3 py-2 font-semibold">Action</th>
          <th className="text-left px-3 py-2 font-semibold">Responsable</th>
          <th className="text-left px-3 py-2 font-semibold">Échéance</th>
          <th className="text-right px-3 py-2 font-semibold w-32"></th>
        </tr>
      </thead>
      <tbody>
        {actions.map((a, i) => (
          <tr
            key={a.id || i}
            className={`border-b border-af last:border-0 transition-colors ${a.completed ? "bg-[#F5EFD8]/40" : ""}`}
            data-testid={`action-row-${a.id || i}`}
          >
            <td className="px-3 py-3">
              <span
                className={`inline-flex items-center justify-center h-6 w-6 rounded-full border-2 transition-all ${
                  a.completed ? "bg-forest border-forest text-white" : "border-af bg-white text-transparent"
                }`}
              >
                <Check className="h-3.5 w-3.5" />
              </span>
            </td>
            <td className={`px-3 py-3 ${a.completed ? "text-af-muted line-through" : "text-af"}`}>{a.action}</td>
            <td className="px-3 py-3 text-af-secondary">{a.responsible || "—"}</td>
            <td className="px-3 py-3 text-af-secondary">{a.deadline || "—"}</td>
            <td className="px-3 py-3 text-right">
              {a.id ? (
                a.completed ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onToggle(a.id, false)}
                    data-testid={`undo-action-${a.id}`}
                    className="text-af-muted hover:text-af hover:bg-af-subtle h-8 px-3 rounded-md text-xs"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" /> Annuler
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => onToggle(a.id, true)}
                    data-testid={`validate-action-${a.id}`}
                    className="bg-forest hover:bg-[#2B1F12] text-white h-8 px-3 rounded-md text-xs"
                  >
                    <Check className="h-3 w-3 mr-1" /> Valider
                  </Button>
                )
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const SummaryTab = ({ summary, summarizing, onRegenerate, onToggleAction }) => {
  if (!summary) {
    return (
      <Card className="p-10 text-center bg-af-card border-af rounded-md">
        <Sparkles className="h-8 w-8 mx-auto text-terracotta mb-3" />
        <p className="text-af-secondary mb-4">Aucun résumé IA généré pour cette transcription.</p>
        <Button onClick={onRegenerate} disabled={summarizing}
          data-testid="generate-summary-btn"
          className="bg-terracotta hover:bg-[#9C7124] text-white rounded-md">
          {summarizing ? "Génération…" : "Générer le résumé IA"}
        </Button>
      </Card>
    );
  }
  return (
    <>
      <Card className="p-8 bg-af-card border-af rounded-md" data-testid="executive-summary-card">
        <SectionHeader icon={Sparkles} title="Résumé exécutif" subtitle="Vue d'ensemble générée par l'IA" />
        <p className="text-af leading-relaxed">{summary.executive_summary}</p>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <BulletCard icon={FileCheck2} title="Décisions prises" items={summary.decisions}
          emptyLabel="Aucune décision enregistrée." marker={{ char: "→", cls: "text-terracotta font-bold shrink-0" }}
          testId="decisions-card" />
        <BulletCard icon={AlertTriangle} title="Risques identifiés" items={summary.risks}
          emptyLabel="Aucun risque identifié." marker={{ char: "!", cls: "text-[#9F1239] font-bold shrink-0" }}
          testId="risks-card" />
      </div>

      <Card className="p-8 bg-af-card border-af rounded-md" data-testid="actions-card">
        <SectionHeader
          icon={ListChecks}
          title="Actions à réaliser"
          subtitle={
            summary.action_items?.length
              ? `${summary.action_items.filter((a) => a.completed).length} / ${summary.action_items.length} validées`
              : "Tâches assignées avec échéances"
          }
        />
        {summary.action_items?.length ? (
          <ActionItemsTable actions={summary.action_items} onToggle={onToggleAction} />
        ) : (
          <p className="text-sm text-af-muted">Aucune action enregistrée.</p>
        )}
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <BulletCard icon={MessageSquare} title="Points ouverts" items={summary.open_points}
          emptyLabel="Aucun point ouvert." testId="open-points-card" />
        <BulletCard icon={Target} title="Prochaines étapes" items={summary.next_steps}
          emptyLabel="Aucune étape définie." testId="next-steps-card" />
      </div>

      <div className="text-xs text-af-muted text-right">
        <button onClick={onRegenerate} disabled={summarizing}
          className="text-terracotta hover:underline" data-testid="regenerate-summary">
          Régénérer
        </button>
      </div>
    </>
  );
};
